import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    Button,
    Paper,
    Breadcrumbs,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
} from '@mui/material';
import { useNotification } from '../../../components/Notification';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Settings as SettingsIcon,
    NavigateNext as NavigateNextIcon,
    DevicesOther as DevicesOtherIcon,
} from '@mui/icons-material';
import Table from '../../../components/Table';
import Input from '../../../components/Input';
import AddDeviceTypeModal from './AddDeviceTypeModal';
import { hasPermission } from '../../../utils/permissions';
import {
    // Device Types
    fetchDeviceTypes,
    fetchDeviceTypeById,
    addDeviceType,
    updateDeviceType,
    deleteDeviceType,
    bulkDeleteDeviceTypes,
    selectDeviceTypes,
    selectDeviceTypesLoading,
    selectDeviceTypesError,
    selectDeviceTypesSuccessMessage,
    selectDeviceTypesTotal,
    selectDeviceTypesPage,
    selectDeviceTypesPageSize,
    selectDeviceTypesSortColumn,
    selectDeviceTypesSortDirection,
    selectDeviceTypesFilterName,
    selectSelectedDeviceType,
    setDeviceTypesPage,
    setDeviceTypesPageSize,
    setDeviceTypesSortColumn,
    setDeviceTypesSortDirection,
    setDeviceTypesFilterName,
    clearDeviceTypesError,
    clearDeviceTypesSuccessMessage,
} from './slice';

function DeviceConfiguration() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { showNotification } = useNotification();

        // Device Types state
    const deviceTypes = useSelector(selectDeviceTypes);
    const deviceTypesLoading = useSelector(selectDeviceTypesLoading);
    const deviceTypesError = useSelector(selectDeviceTypesError);
    const deviceTypesSuccessMessage = useSelector(selectDeviceTypesSuccessMessage);
    const deviceTypesTotal = useSelector(selectDeviceTypesTotal);
    const deviceTypesPage = useSelector(selectDeviceTypesPage);
    const deviceTypesPageSize = useSelector(selectDeviceTypesPageSize);
    const deviceTypesSortColumn = useSelector(selectDeviceTypesSortColumn);
    const deviceTypesSortDirection = useSelector(selectDeviceTypesSortDirection);
    const deviceTypesFilterName = useSelector(selectDeviceTypesFilterName);
    const selectedDeviceType = useSelector(selectSelectedDeviceType);

        // Local state for Device Types
    const [deviceTypeSelectedIds, setDeviceTypeSelectedIds] = useState([]);
    const [showDeviceTypeDeleteDialog, setShowDeviceTypeDeleteDialog] = useState(false);
    const [deviceTypeToDelete, setDeviceTypeToDelete] = useState(null);
    const [showDeviceTypeBulkDeleteDialog, setShowDeviceTypeBulkDeleteDialog] = useState(false);
    const [showAddDeviceTypeDialog, setShowAddDeviceTypeDialog] = useState(false);
    const [showEditDeviceTypeDialog, setShowEditDeviceTypeDialog] = useState(false);

        // Check permissions
    const canAddDeviceType = hasPermission('devicetype', 'addDeviceType');
    const canEditDeviceType = hasPermission('devicetype', 'editDeviceType');
    const canDeleteDeviceType = hasPermission('devicetype', 'deleteDeviceType');

    // Fetch Device Types
    useEffect(() => {
        dispatch(fetchDeviceTypes({
            page: deviceTypesPage,
            pageSize: deviceTypesPageSize,
            sortColumn: deviceTypesSortColumn,
            sortDirection: deviceTypesSortDirection,
            filterName: deviceTypesFilterName,
        }));
    }, [dispatch, deviceTypesPage, deviceTypesPageSize, deviceTypesSortColumn, deviceTypesSortDirection, deviceTypesFilterName]);

        // Device Types error notification
    useEffect(() => {
        if (deviceTypesError) {
            showNotification(deviceTypesError, 'error');
            dispatch(clearDeviceTypesError());
        }
    }, [deviceTypesError, dispatch, showNotification]);

    // Device Types success notification
    useEffect(() => {
        if (deviceTypesSuccessMessage) {
            showNotification(deviceTypesSuccessMessage, 'success');
            dispatch(clearDeviceTypesSuccessMessage());
            dispatch(fetchDeviceTypes({
                page: deviceTypesPage,
                pageSize: deviceTypesPageSize,
                sortColumn: deviceTypesSortColumn,
                sortDirection: deviceTypesSortDirection,
                filterName: deviceTypesFilterName,
            }));
        }
    }, [deviceTypesSuccessMessage, dispatch, showNotification, deviceTypesPage, deviceTypesPageSize, deviceTypesSortColumn, deviceTypesSortDirection, deviceTypesFilterName]);    const escapeHtml = (text) => {
        if (!text) return '-';
        const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' };
        return text.replace(/[&<>"']/g, (m) => map[m]);
    };

    // ==================== Device Types Handlers ====================
    const deviceTypeColumns = [
        { key: 'name', label: t('deviceConfig.deviceTypes.table.name') },
        { key: 'description', label: t('deviceConfig.deviceTypes.table.description'), render: (value) => escapeHtml(value) },
    ];

    const handleEditDeviceType = (row) => {
        dispatch(fetchDeviceTypeById(row.id));
        setShowEditDeviceTypeDialog(true);
    };

    const handleDeleteDeviceType = (row) => {
        setDeviceTypeToDelete(row);
        setShowDeviceTypeDeleteDialog(true);
    };

    const handleConfirmDeleteDeviceType = () => {
        if (deviceTypeToDelete?.id) {
            dispatch(deleteDeviceType(deviceTypeToDelete.id));
        }
        setShowDeviceTypeDeleteDialog(false);
        setDeviceTypeToDelete(null);
    };

    const handleBulkDeleteDeviceTypes = () => {
        setShowDeviceTypeBulkDeleteDialog(true);
    };

    const handleConfirmBulkDeleteDeviceTypes = () => {
        dispatch(bulkDeleteDeviceTypes(deviceTypeSelectedIds));
        setShowDeviceTypeBulkDeleteDialog(false);
        setDeviceTypeSelectedIds([]);
    };

    const handleAddDeviceType = () => setShowAddDeviceTypeDialog(true);
    const handleCloseAddDeviceType = () => setShowAddDeviceTypeDialog(false);
    const handleConfirmAddDeviceType = (data) => {
        dispatch(addDeviceType(data));
        setShowAddDeviceTypeDialog(false);
    };

    const handleCloseEditDeviceType = () => setShowEditDeviceTypeDialog(false);
    const handleConfirmEditDeviceType = (data) => {
        dispatch(updateDeviceType(data));
        setShowEditDeviceTypeDialog(false);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header Section */}
            <Box sx={{ px: 3, pt: 3, pb: 2, backgroundColor: '#ffffff' }}>
                <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <DevicesOtherIcon fontSize="small" />
                        <Typography variant="body2">{t('deviceConfig.breadcrumb.deviceManagement')}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.primary">{t('deviceConfig.breadcrumb.configuration')}</Typography>
                </Breadcrumbs>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SettingsIcon />
                        <Typography variant="h5" component="h1" fontWeight="bold" sx={{ textAlign: 'left' }}>
                            {t('deviceConfig.deviceTypes.title')}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Content */}
            <Paper elevation={0} sx={{ borderRadius: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#ffffff', mt: 2 }}>
                {/* Filter and Action Buttons */}
                <Box sx={{ px: 3, pt: 2, pb: 2, borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                    <Input
                        label={t('deviceConfig.deviceTypes.filterByName')}
                        value={deviceTypesFilterName}
                        onChange={(e) => dispatch(setDeviceTypesFilterName(e.target.value))}
                        placeholder={t('deviceConfig.deviceTypes.filterByNamePlaceholder')}
                        id="deviceTypesFilterName"
                        name="deviceTypesFilterName"
                        data-testid="deviceConfig-input-deviceTypesFilterName"
                        sx={{ width: 320 }}
                    />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {canAddDeviceType && (
                            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleAddDeviceType}
                                id="addDeviceType" name="addDeviceType" data-testid="deviceConfig-button-addDeviceType">
                                {t('deviceConfig.deviceTypes.addDeviceType')}
                            </Button>
                        )}
                        {canDeleteDeviceType && (
                            <Button variant="contained" color="error" startIcon={<DeleteIcon />} onClick={handleBulkDeleteDeviceTypes}
                                disabled={deviceTypeSelectedIds.length === 0}
                                id="deleteSelectedDeviceTypes" name="deleteSelectedDeviceTypes" data-testid="deviceConfig-button-deleteSelectedDeviceTypes">
                                {t('deviceConfig.deviceTypes.deleteSelected')}
                            </Button>
                        )}
                    </Box>
                </Box>

                {/* Table */}
                <Box sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: 3, backgroundColor: '#ffffff' }}>
                    <Table
                        id="deviceTypes-table"
                        columns={deviceTypeColumns}
                        data={deviceTypes}
                        selectable={true}
                        selectedIds={deviceTypeSelectedIds}
                        onSelectionChange={setDeviceTypeSelectedIds}
                        isRowSelectable={(row) => !row.isDefault}
                        onEdit={canEditDeviceType ? handleEditDeviceType : null}
                        onDelete={canDeleteDeviceType ? handleDeleteDeviceType : null}
                        isRowDeletable={(row) => !row.isDefault}
                        loading={deviceTypesLoading}
                        emptyMessage={t('deviceConfig.deviceTypes.noData')}
                        idField="id"
                        pagination={true}
                        page={deviceTypesPage}
                        pageSize={deviceTypesPageSize}
                        totalItems={deviceTypesTotal}
                        onPageChange={(p) => dispatch(setDeviceTypesPage(p))}
                        onPageSizeChange={(s) => dispatch(setDeviceTypesPageSize(s))}
                        pageSizeOptions={[5, 10, 20, 50, 100]}
                        sortable={true}
                        sortColumn={deviceTypesSortColumn}
                        sortDirection={deviceTypesSortDirection}
                        onSortChange={(col, dir) => {
                            dispatch(setDeviceTypesSortColumn(col));
                            dispatch(setDeviceTypesSortDirection(dir));
                        }}
                        sortableColumns={['name']}
                    />
                </Box>
            </Paper>

            {/* Device Type Delete Dialog */}
            <Dialog open={showDeviceTypeDeleteDialog} onClose={() => setShowDeviceTypeDeleteDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
                    <DeleteIcon />
                    {t('deviceConfig.deviceTypes.deleteDialog.title')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('deviceConfig.deviceTypes.deleteDialog.message', { name: deviceTypeToDelete?.name || '' })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setShowDeviceTypeDeleteDialog(false)} variant="outlined">
                        {t('deviceConfig.deviceTypes.deleteDialog.cancel')}
                    </Button>
                    <Button onClick={handleConfirmDeleteDeviceType} variant="contained" color="error" startIcon={<DeleteIcon />}>
                        {t('deviceConfig.deviceTypes.deleteDialog.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Device Type Bulk Delete Dialog */}
            <Dialog open={showDeviceTypeBulkDeleteDialog} onClose={() => setShowDeviceTypeBulkDeleteDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
                    <DeleteIcon />
                    {t('deviceConfig.deviceTypes.bulkDeleteDialog.title')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('deviceConfig.deviceTypes.bulkDeleteDialog.message', { count: deviceTypeSelectedIds.length })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setShowDeviceTypeBulkDeleteDialog(false)} variant="outlined">
                        {t('deviceConfig.deviceTypes.bulkDeleteDialog.cancel')}
                    </Button>
                    <Button onClick={handleConfirmBulkDeleteDeviceTypes} variant="contained" color="error" startIcon={<DeleteIcon />}>
                        {t('deviceConfig.deviceTypes.bulkDeleteDialog.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Device Type Modal */}
            <AddDeviceTypeModal open={showAddDeviceTypeDialog} onClose={handleCloseAddDeviceType} onConfirm={handleConfirmAddDeviceType} />

            {/* Edit Device Type Modal */}
            <AddDeviceTypeModal
                open={showEditDeviceTypeDialog}
                onClose={handleCloseEditDeviceType}
                onConfirm={handleConfirmEditDeviceType}
                deviceTypeData={selectedDeviceType}
                editMode={true}
            />

        </Box>
    );
}

export default DeviceConfiguration;
